/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/presentationList.config.json";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_COSTUME_TYPE_PARTITION_INVENTORY_REPORT } from "../graphql";
import { VApplication } from "../../../application";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IInventoryMethods extends IPluginMethodMap {
  getInventoryProps(args: any): Promise<any>;
}

export class InventoryService implements IAgentPlugin {
  readonly methods: IInventoryMethods;

  constructor() {
    this.methods = {
      getInventoryProps: this.getInventoryProps.bind(this),
    };
  }

  async getInventoryProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    
    const presentationId = args?.presentationId || args?.id;
    if (!presentationId || !client) {
      return {
        ...config,
        ultUsers: [],
        isEmpty: true,
        totalCount: 0,
        totalPages: 1,
        activePage: 1,
        pageSize: args?.size ?? 10,
      };
    }

    const baseFilter: any = {
      presentationId,
      name: {
        contains: args?.commonSearch || "",
      },
    };

    const options = {
      limit: args?.size ?? 100,
      skip: args?.offset ?? 0,
    };

    try {
      const [defaultRes, specialRes, specificRes] = await Promise.all([
        client.query({
          query: GET_COSTUME_TYPE_PARTITION_INVENTORY_REPORT,
          variables: {
            filter: {
              ...baseFilter,
              partition: "default",
            },
            options,
          },
          fetchPolicy: "network-only",
          errorPolicy: "all",
        }),
        client.query({
          query: GET_COSTUME_TYPE_PARTITION_INVENTORY_REPORT,
          variables: {
            filter: {
              ...baseFilter,
              partition: "special",
            },
            options,
          },
          fetchPolicy: "network-only",
          errorPolicy: "all",
        }),
        client.query({
          query: GET_COSTUME_TYPE_PARTITION_INVENTORY_REPORT,
          variables: {
            filter: {
              ...baseFilter,
              partition: "specific",
            },
            options,
          },
          fetchPolicy: "network-only",
          errorPolicy: "all",
        }),
      ]);

      const defaultContent = defaultRes?.data?.getCostumeTypePartitionInventoryReport?.content || [];
      const specialContent = specialRes?.data?.getCostumeTypePartitionInventoryReport?.content || [];
      const specificContent = specificRes?.data?.getCostumeTypePartitionInventoryReport?.content || [];

      const itemsMap: { [id: string]: any } = {};

      const processPartition = (content: any[], partitionKey: "default" | "special" | "specific") => {
        (content || []).forEach((item: any) => {
          const id = item.id || item.costumeType;
          if (!itemsMap[id]) {
            itemsMap[id] = {
              id,
              costumeType: item.costumeType || id,
              default: { inventory: 0, assigned: 0, remaining: 0 },
              special: { inventory: 0, assigned: 0, remaining: 0 },
              specific: { inventory: 0, assigned: 0, remaining: 0 },
            };
          }
          itemsMap[id][partitionKey] = {
            inventory: item.inventory ?? 0,
            assigned: item.assigned ?? 0,
            remaining: item.remaining ?? 0,
          };
        });
      };

      processPartition(defaultContent, "default");
      processPartition(specialContent, "special");
      processPartition(specificContent, "specific");

      const _rowData = Object.values(itemsMap);
      const totalCount = defaultRes?.data?.getCostumeTypePartitionInventoryReport?.count ?? _rowData.length;

      return {
        ...config,
        ultUsers: _rowData,
        isEmpty: _rowData.length <= 0,
        activePage: 1,
        totalCount: totalCount,
        totalPages: 1,
        pageSize: args?.size ?? 10,
      };
    } catch (err: any) {
      console.log("error fetching partition inventory report", err);
    }

    return {
      ...config,
      ultUsers: [],
      isEmpty: true,
      totalCount: 0,
      totalPages: 1,
      activePage: 1,
      pageSize: args?.size ?? 10,
    };
  }
}
